/**
 * La cella «Ritiro» del Portafoglio: mostra lo stato **e** lo cambia.
 *
 * Tre stati, ma se ne marca uno solo (modello deciso con Enrico il 18-08-2026):
 *
 * - **tassativa** — il ritiro è già fissato. **Non si marca: si deriva.** Se
 *   fosse un'opzione da spuntare esisterebbero tassative senza ritiro, cioè
 *   promesse scoperte; derivandola lo stato non può mentire.
 * - **standard** — si produce entro la data, il ritiro non è ancora fissato.
 * - **a chiamata** — riempie il magazzino, il ritiro lo chiederà il cliente.
 *
 * Il gesto marca la **politica** (standard o a chiamata) e può valere per la
 * singola commessa o per **tutto il cliente**: WONDERFOOD ritira a rate *come
 * cliente* e ha undici commesse aperte — marcarle una a una non farebbe
 * accumulare il lavoro, e le nuove arriverebbero non marcate.
 *
 * Perché era a zero su duecento: il meccanismo esisteva in M3 dal giro di giugno
 * ma **il gesto non era in nessuna interfaccia** — la funzione che scrive stava in
 * una vista non più raggiungibile, e la colonna per cliente esisteva in DB senza
 * essere esposta. Stesso pattern della spunta «materiale di consumo».
 */

import { useRef, useState } from "react";
import { CalendarCheck, ChevronDown, Loader2, PhoneCall, Truck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useM1RitiroCliente, useM1RitiroCommessa } from "@/hooks/useM1";
import { cn } from "@/lib/utils";
import type { M1OrdineRaggruppato } from "@/types/api";

/** Il nome leggibile di una politica, usato nei bottoni e nei messaggi. */
const ETICHETTA: Record<string, string> = {
  standard: "standard",
  a_chiamata: "a chiamata",
  programmato: "programmato",
};

/** Quanto spazio serve al pannello, a occhio e per eccesso: con la riga in più
 *  della cancellazione arriva a ~250 px. Serve solo a scegliere il verso. */
const ALTEZZA_PANNELLO = 260;

function formatDataBreve(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short" });
}

export function RitiroCella({
  ordine,
  onApplicato,
}: {
  ordine: M1OrdineRaggruppato;
  /** Il messaggio da mostrare in pagina: chi marca un cliente deve sapere quante
   *  commesse ha toccato, altrimenti crede di averne cambiata una. */
  onApplicato: (messaggio: string) => void;
}) {
  const [aperto, setAperto] = useState(false);
  /** Verso di apertura. Sulle ultime righe della tabella il pannello finiva
   *  **sotto il bordo dello schermo** — misurato: fondo a 975 px con la finestra
   *  a 900 — e da fuori sembrava che il clic non avesse fatto niente. Si decide
   *  all'apertura, dove si conosce la posizione vera del bottone. */
  const [verso, setVerso] = useState<"giu" | "su">("giu");
  const bottone = useRef<HTMLButtonElement>(null);
  const [nota, setNota] = useState("");
  const perCommessa = useM1RitiroCommessa();
  const perCliente = useM1RitiroCliente();
  const inCorso = perCommessa.isPending || perCliente.isPending;

  function marca(
    modalita: "standard" | "a_chiamata" | "nessuna",
    ambito: "commessa" | "cliente",
  ) {
    const body = { modalita_ritiro: modalita, nota: nota.trim() || null };
    const comune = {
      onSuccess: (r: {
        commesse_interessate: number;
        riferimento: string;
        stato_ritiro: string;
        modalita_ritiro: string;
        cancellata: boolean;
      }) => {
        setAperto(false);
        setNota("");
        const nome = (m: string) => (m === "a_chiamata" ? "a chiamata" : "standard");
        if (r.cancellata) {
          // Cancellare non è «marcare standard»: va detto **cosa vale adesso**, o
          // chi ha cliccato non sa se ha ottenuto qualcosa.
          onApplicato(
            ambito === "cliente"
              ? `${r.riferimento}: nessuna politica dichiarata — le sue ${r.commesse_interessate} commesse aperte tornano a standard.`
              : `Commessa ${r.riferimento}: marcatura rimossa, ora segue il cliente (${nome(r.modalita_ritiro)}).`,
          );
          return;
        }
        onApplicato(
          ambito === "cliente"
            ? `${r.riferimento}: ${nome(modalita)} — ${r.commesse_interessate} commesse aperte toccate.`
            : `Commessa ${r.riferimento}: ${r.stato_ritiro === "tassativa" ? "resta tassativa, il ritiro è già fissato" : nome(modalita)}.`,
        );
      },
    };
    if (ambito === "cliente") {
      perCliente.mutate({ codice: ordine.cliente_codice, body }, comune);
    } else {
      perCommessa.mutate({ numero: ordine.numero_ordine, body }, comune);
    }
  }

  return (
    <div className="relative">
      <button
        ref={bottone}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          const spazioSotto = window.innerHeight - (bottone.current?.getBoundingClientRect().bottom ?? 0);
          setVerso(spazioSotto < ALTEZZA_PANNELLO ? "su" : "giu");
          setAperto((v) => !v);
        }}
        className="group/rit flex items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-muted"
        title="Cambia la politica di ritiro"
      >
        <Badge ordine={ordine} />
        <ChevronDown className="size-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/rit:opacity-100" />
      </button>

      {aperto && (
        <>
          {/* Chiude cliccando fuori, senza libreria: il pannello è piccolo. */}
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => {
              e.stopPropagation();
              setAperto(false);
            }}
          />
          <div
            className={cn(
              "absolute left-0 z-50 w-[19rem] space-y-2 rounded-lg border bg-popover p-3 shadow-lg",
              verso === "su" ? "bottom-full mb-1" : "top-full mt-1",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {ordine.stato_ritiro === "tassativa" ? (
              <p className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] leading-snug text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
                Questa commessa è <b>tassativa</b>: il ritiro è già fissato
                {ordine.ritiro_fissato_il ? ` per il ${formatDataBreve(ordine.ritiro_fissato_il)}` : ""}.
                Lo stato non si marca — si ricava dal ritiro. Quello che scegli qui
                vale <b>se il ritiro salta</b>.
              </p>
            ) : (
              <p className="text-[11px] leading-snug text-muted-foreground">
                Marca la <b>politica</b>: diventa <b>tassativa</b> da sé quando il
                ritiro viene fissato.
              </p>
            )}

            <label className="block space-y-1">
              <span className="text-[10.5px] font-medium text-muted-foreground">
                Perché (resta come traccia)
              </span>
              <input
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="es. ritira a rate, ce l'ha chiesto lui"
                className="w-full rounded border bg-background px-2 py-1 text-xs"
              />
            </label>

            <div className="space-y-1.5">
              <Riga
                titolo="Solo questa commessa"
                azioni={[
                  {
                    label: "Standard",
                    onClick: () => marca("standard", "commessa"),
                    attivo:
                      ordine.modalita_ritiro === "standard" && ordine.ritiro_marcato_su_commessa,
                  },
                  {
                    label: "A chiamata",
                    onClick: () => marca("a_chiamata", "commessa"),
                    attivo:
                      ordine.modalita_ritiro === "a_chiamata" && ordine.ritiro_marcato_su_commessa,
                  },
                ]}
                inCorso={inCorso}
              />
              {/* La via d'uscita, e c'è **solo se c'è qualcosa da togliere**: un
                  bottone che non fa niente insegna a non fidarsi degli altri.
                  Dice anche dove si torna, perché «come il cliente» senza dire
                  cosa dice il cliente chiede fiducia al buio. */}
              {ordine.ritiro_marcato_su_commessa && (
                <Riga
                  titolo="Questa commessa è marcata a mano"
                  azioni={[
                    {
                      label: ordine.ritiro_politica_cliente
                        ? `Torna al cliente (${ETICHETTA[ordine.ritiro_politica_cliente]})`
                        : "Togli la marcatura",
                      onClick: () => marca("nessuna", "commessa"),
                    },
                  ]}
                  inCorso={inCorso}
                />
              )}
              <Riga
                titolo={`Tutte le commesse di ${ordine.cliente_ragione_sociale}`}
                azioni={[
                  {
                    label: "Standard",
                    onClick: () => marca("standard", "cliente"),
                    attivo: ordine.ritiro_politica_cliente === "standard",
                  },
                  {
                    label: "A chiamata",
                    onClick: () => marca("a_chiamata", "cliente"),
                    attivo: ordine.ritiro_politica_cliente === "a_chiamata",
                  },
                  ...(ordine.ritiro_politica_cliente
                    ? [{ label: "Togli", onClick: () => marca("nessuna", "cliente") }]
                    : []),
                ]}
                inCorso={inCorso}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Riga({
  titolo,
  azioni,
  inCorso,
}: {
  titolo: string;
  azioni: { label: string; onClick: () => void; attivo?: boolean }[];
  inCorso: boolean;
}) {
  return (
    <div className="space-y-1">
      <p className="truncate text-[10.5px] text-muted-foreground">{titolo}</p>
      <div className="flex gap-1.5">
        {azioni.map((a) => (
          <Button
            key={a.label}
            type="button"
            size="sm"
            variant={a.attivo ? "default" : "outline"}
            disabled={inCorso}
            onClick={a.onClick}
            className="h-7 flex-1 text-[11px]"
          >
            {inCorso ? <Loader2 className="size-3 animate-spin" /> : a.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * Il badge dello stato **effettivo**. Tre forme distinte, perché tre stati che si
 * somigliano non si distinguono a colpo d'occhio in una tabella da 25 righe.
 */
export function Badge({ ordine }: { ordine: M1OrdineRaggruppato }) {
  if (ordine.stato_ritiro === "tassativa") {
    const scaduto = ordine.ritiro_scaduto;
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold",
          scaduto
            ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
            : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
        )}
        title={
          scaduto
            ? "Il ritiro era fissato e la data è passata: o la merce è uscita e nessuno ha chiuso la commessa, o il ritiro è saltato"
            : "Il ritiro è fissato: la promessa è coperta. Stato derivato, non marcato."
        }
      >
        <CalendarCheck className="size-3" />
        {ordine.ritiro_fissato_il ? formatDataBreve(ordine.ritiro_fissato_il) : "fissato"}
        {scaduto && " ⚠"}
      </span>
    );
  }
  if (ordine.stato_ritiro === "a_chiamata") {
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-800 dark:bg-orange-950 dark:text-orange-300"
        title="Il cliente ritira quando gli serve: la merce resta nel nostro magazzino"
      >
        <PhoneCall className="size-3" />
        a chiamata
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
      title="Si produce entro la data prevista, il ritiro non è ancora fissato"
    >
      <Truck className="size-3" />
      standard
    </span>
  );
}
